// Atalhos novos da tela (5.1): L abre e fecha o timelapse do dia; P vai ao próximo pedido de
// aprovação ou pergunta (seleciona o agente, e o cartão abre no painel dele); [ esconde ou mostra
// a lateral; ] faz o mesmo com o feed; setas e WASD movem a câmera uma casa por toque, andando
// enquanto a tecla estiver segurada; 0 faz a visão geral, como O.
//
// As teclas da barra (O, M, E, H, + e −) ficam em toolbar.ts e as de janela (/, j, k, f, t, ?) em
// main.ts. Aqui vêm também as funções puras que o teste confere; o resto liga o document.

import type { Agent, OfficeSnapshot } from '../core/types.js';
import { atalhoBloqueado, normalizaTecla } from './toolbar.js';

/** Casas que cada tecla de câmera anda: [x, y]. Setas e WASD. */
const DIRECAO: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  a: [-1, 0],
  ArrowRight: [1, 0],
  d: [1, 0],
  ArrowUp: [0, -1],
  w: [0, -1],
  ArrowDown: [0, 1],
  s: [0, 1],
};

/** Quanto a câmera anda enquanto a tecla está segurada: a 1ª casa vem na hora; depois, espera e passo. */
const ATRASO_INICIAL_MS = 220;
const INTERVALO_MS = 90;

/** Direção da tecla de câmera (sem caixa), ou undefined se a tecla não move a câmera. */
export function direcaoDaTecla(tecla: string): [number, number] | undefined {
  const normal = normalizaTecla(tecla);
  return Object.hasOwn(DIRECAO, normal) ? DIRECAO[normal] : undefined;
}

/** Próximo item da fila, em volta: sem atual (ou atual fora da fila), o primeiro. Fila vazia: undefined. */
export function proximoDaFila(ids: readonly string[], atual: string | null): string | undefined {
  if (!ids.length) return undefined;
  const posicao = atual ? ids.indexOf(atual) : -1;
  return ids[(posicao + 1) % ids.length];
}

/**
 * Quem espera por você, sem repetir: primeiro quem tem pedido de aprovação ou pergunta (na ordem
 * em que chegaram), depois quem está com a mão levantada. Id que não é de agente atual não entra.
 */
export function quemPrecisa(snapshot: Pick<OfficeSnapshot, 'agents' | 'permissions'> | null): string[] {
  if (!snapshot) return [];
  const existentes = new Set(snapshot.agents.map((a) => a.id));
  const pedidos = snapshot.permissions.map((p) => p.agentId);
  const esperando = snapshot.agents.filter((a) => a.status === 'waiting').map((a) => a.id);
  return [...new Set([...pedidos, ...esperando])].filter((id) => existentes.has(id));
}

/** Agentes com um status (o contador de shells do topo usa 'shell'), na ordem da lista. */
export function agentesComStatus(agents: readonly Pick<Agent, 'id' | 'status'>[], status: Agent['status']): string[] {
  return agents.filter((a) => a.status === status).map((a) => a.id);
}

/**
 * Elementos em que os atalhos de tela não agem: janelas de conversa e de guia, diálogos e a
 * lista de agentes (que usa as setas para si). O timelapse fica de fora: o L fecha ele.
 */
const JANELAS = '.vivo, .guia, [role="dialog"], [aria-modal="true"]';

function dentroDeJanela(foco: Element | null): boolean {
  return Boolean(foco?.closest(JANELAS));
}

/** O que os atalhos precisam do resto da interface (main.ts e campus.ts). */
export interface AcoesAtalhos {
  campus: { moveCamera(casasX: number, casasY: number): void; fitAll(): void };
  /** L: abre o timelapse do dia, ou fecha se já está aberto. */
  timelapse(): void;
  /** Ids de quem espera por você (P), na ordem. */
  esperam(): string[];
  /** Agente selecionado agora, para o P saber de quem seguir. */
  agenteAtual(): string | null;
  /** P: seleciona o agente e abre o cartão do pedido dele. */
  selecionaAgente(id: string): void;
}

const teclasSegure = new Map<string, [number, number]>();
let quadroAgendado = 0;
let proximoPasso = 0;
let montado = false;

/** Liga os atalhos de tela uma vez, no document (depois de montaBarra, que é a outra metade das teclas). */
export function montaAtalhos(acoes: AcoesAtalhos): void {
  if (montado) return;
  montado = true;
  document.addEventListener('keydown', (evento) => aoTecla(evento, acoes));
  document.addEventListener('keyup', (evento) => {
    teclasSegure.delete(normalizaTecla(evento.key));
  });
  // trocar de aba com a tecla segurada não deixa a câmera andando sozinha
  window.addEventListener('blur', () => teclasSegure.clear());
}

function aoTecla(evento: KeyboardEvent, acoes: AcoesAtalhos): void {
  // a tela de login fica por cima do escritório: sem sessão, nenhum atalho
  const app = document.getElementById('app');
  if (!app || app.hidden) return;
  if (atalhoBloqueado(evento, document.activeElement as HTMLElement | null)) return;
  const foco = document.activeElement;
  if (dentroDeJanela(foco)) return;

  const direcao = direcaoDaTecla(evento.key);
  if (direcao) {
    // as setas andam pela lista da lateral quando o foco está nela
    if (foco?.closest('#projetos')) return;
    evento.preventDefault();
    if (!evento.repeat) {
      teclasSegure.set(normalizaTecla(evento.key), direcao);
      acoes.campus.moveCamera(direcao[0], direcao[1]);
      proximoPasso = performance.now() + ATRASO_INICIAL_MS;
      if (!quadroAgendado) quadroAgendado = requestAnimationFrame(andaComTeclas(acoes));
    }
    return;
  }

  switch (normalizaTecla(evento.key)) {
    case 'l':
      acoes.timelapse();
      break;
    case 'p': {
      const id = proximoDaFila(acoes.esperam(), acoes.agenteAtual());
      if (id) acoes.selecionaAgente(id);
      break;
    }
    case '[':
      document.body.classList.toggle('sem-lateral');
      break;
    case ']':
      document.body.classList.toggle('sem-feed');
      break;
    case '0':
      acoes.campus.fitAll();
      break;
    default:
      return;
  }
  evento.preventDefault();
}

/** Enquanto houver tecla de câmera segurada, anda uma casa a cada passo (somando as direções). */
function andaComTeclas(acoes: AcoesAtalhos): FrameRequestCallback {
  const quadro = (agora: number) => {
    if (teclasSegure.size === 0) {
      quadroAgendado = 0;
      return;
    }
    if (agora >= proximoPasso) {
      let x = 0;
      let y = 0;
      for (const [dx, dy] of teclasSegure.values()) {
        x += dx;
        y += dy;
      }
      if (x || y) acoes.campus.moveCamera(Math.sign(x), Math.sign(y));
      proximoPasso = agora + INTERVALO_MS;
    }
    quadroAgendado = requestAnimationFrame(quadro);
  };
  return quadro;
}
